'use client'

import React from 'react'

import { stegaClean } from '@sanity/client/stega'

import { Icon } from '@/app/components/Icon'
import { Link, LinkProps } from '@/app/components/Link'
import { buildTrackingData, trackElementClick, TrackingLocation } from '@/app/utils/analytics'
import { cn } from '@/app/utils/style'
import { Button as ButtonRaw, Icon as IconRaw } from '@/sanity.types'
import type { DeepRequiredNullable } from '@/types/utils'

function isLinkElement(element: React.ReactElement): element is React.ReactElement<LinkProps> {
  return element.type === Link
}

type ButtonWithNulls = DeepRequiredNullable<ButtonRaw>
type IconWithNulls = DeepRequiredNullable<IconRaw>

type IconWithDeref = IconWithNulls

type ButtonWithDeref = Omit<ButtonWithNulls, 'link' | 'icon'> & {
  link?: LinkProps['link'] | null
  icon?: IconWithDeref | null
}

type ButtonPropsBase = {
  button: ButtonWithDeref
  tracking: TrackingLocation
  className?: string
}

type ButtonPropsWithAsChild = ButtonPropsBase & {
  asChild: true
  children: React.ReactElement<LinkProps>
}

type ButtonPropsWithoutAsChild = ButtonPropsBase &
  React.ComponentProps<'button'> & {
    asChild?: false
    children?: React.ReactNode
  }

export type ButtonProps = ButtonPropsWithAsChild | ButtonPropsWithoutAsChild

const variantClasses = {
  primary:
    'bg-neutral-900 text-neutral-50 hover:bg-primary-600 active:bg-primary-200 disabled:bg-neutral-200',
  secondary:
    'bg-transparent text-neutral-900 shadow-[inset_0_0_0_3px_var(--color-neutral-900)] hover:shadow-[inset_0_0_0_3px_var(--color-primary-200)] active:shadow-[inset_0_0_0_3px_var(--color-primary-600)] active:text-primary-600 disabled:shadow-[inset_0_0_0_3px_var(--color-neutral-500)] disabled:text-neutral-500',
  tertiary: 'bg-transparent text-neutral-900 active:text-blue-600',
}

export const sizeClasses = {
  sm: 'px-4xl py-md size-button-sm gap-lg leading-[20px]',
  md: 'px-5xl py-md size-button-md gap-lg leading-[24px]',
  lg: 'px-6xl py-md size-button-lg gap-lg leading-[28px]',
} as const

type ButtonSize = keyof typeof sizeClasses
type Breakpoint = 'sm' | 'md' | 'lg' | 'xl' | '2xl'

// Pre-generate all possible responsive size class combinations for Tailwind scanning
const responsiveSizeClasses = {
  // sm breakpoint
  'sm:sm': 'sm:px-4xl sm:py-md sm:size-button-sm sm:gap-sm sm:leading-[20px]',
  'sm:md': 'sm:px-5xl sm:py-md sm:size-button-md sm:gap-md sm:leading-[24px]',
  'sm:lg': 'sm:px-6xl sm:py-md sm:size-button-lg sm:gap-lg sm:leading-[28px]',
  // md breakpoint
  'md:sm': 'md:px-4xl md:py-md md:size-button-sm md:gap-sm md:leading-[20px]',
  'md:md': 'md:px-5xl md:py-md md:size-button-md md:gap-md md:leading-[24px]',
  'md:lg': 'md:px-6xl md:py-md md:size-button-lg md:gap-lg md:leading-[28px]',
  // lg breakpoint
  'lg:sm': 'lg:px-4xl lg:py-md lg:size-button-sm lg:gap-sm lg:leading-[20px]',
  'lg:md': 'lg:px-5xl lg:py-md lg:size-button-md lg:gap-md lg:leading-[24px]',
  'lg:lg': 'lg:px-6xl lg:py-md lg:size-button-lg lg:gap-lg lg:leading-[28px]',
  // xl breakpoint
  'xl:sm': 'xl:px-4xl xl:py-md xl:size-button-sm xl:gap-sm xl:leading-[20px]',
  'xl:md': 'xl:px-5xl xl:py-md xl:size-button-md xl:gap-md xl:leading-[24px]',
  'xl:lg': 'xl:px-6xl xl:py-md xl:size-button-lg xl:gap-lg xl:leading-[28px]',
  // 2xl breakpoint
  '2xl:sm': '2xl:px-4xl 2xl:py-md 2xl:size-button-sm 2xl:gap-sm 2xl:leading-[20px]',
  '2xl:md': '2xl:px-5xl 2xl:py-md 2xl:size-button-md 2xl:gap-md 2xl:leading-[24px]',
  '2xl:lg': '2xl:px-6xl 2xl:py-md 2xl:size-button-lg 2xl:gap-lg 2xl:leading-[28px]',
} as const

/**
 * Generate responsive button size classes for use in PortableText or other contexts.
 * All class combinations are pre-generated for Tailwind's build-time scanning.
 *
 * @example
 * buttonSizeClasses({ lg: 'md' }) // Only apply md size on lg breakpoint
 * buttonSizeClasses({ md: 'md', lg: 'lg' }) // md on md+, lg on lg+
 */
export function buttonSizeClasses(responsive: Partial<Record<Breakpoint, ButtonSize>>): string {
  const classes: string[] = []

  for (const [breakpoint, size] of Object.entries(responsive) as [Breakpoint, ButtonSize][]) {
    const key = `${breakpoint}:${size}` as keyof typeof responsiveSizeClasses
    if (responsiveSizeClasses[key]) {
      classes.push(responsiveSizeClasses[key])
    }
  }

  return classes.join(' ')
}

const iconSizes = {
  sm: 15,
  md: 20,
  lg: 25,
}

export const Button = function ({
  button,
  tracking,
  className,
  asChild = false,
  children,
  ...buttonProps
}: ButtonProps) {
  if (!button) return null

  // Clean stega encoding from entire Sanity object
  const cleanButton = stegaClean(button)

  // Handle click tracking for button elements (not links)
  const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    const textContent =
      typeof children === 'string' ? children : cleanButton.link?.label || 'Button'
    const trackingData = buildTrackingData('button', textContent, tracking)
    trackElementClick(trackingData)

    // Call original onClick if provided
    if ('onClick' in buttonProps && buttonProps.onClick) {
      buttonProps.onClick(event)
    }
  }

  const variant = cleanButton.variant || 'primary'
  const size = cleanButton.size || 'md'
  const iconPlacement = cleanButton.iconPlacement || 'none'
  const hasIcon = iconPlacement !== 'none' && !!cleanButton.icon

  const tertiaryHeightClasses = {
    sm: '!h-[26px]',
    md: '!h-[31px]',
    lg: '!h-[38px]',
  }

  const tertiaryUnderlineOffset = {
    sm: '3px',
    md: '3.5px',
    lg: '5px',
  }

  const finalClassName = cn(
    'group inline-flex items-center justify-center rounded-xl font-bold whitespace-nowrap cursor-pointer transition-[color,background-color,box-shadow] focus:outline-none',
    variantClasses[variant],
    sizeClasses[size],
    variant === 'tertiary' && ['!px-0', '!py-0', tertiaryHeightClasses[size]],
    className,
  )

  // Helper to render button content with optional icon
  const renderContent = (label: React.ReactNode) => {
    // Wrap only text in span with underline for tertiary variant
    const textElement =
      variant === 'tertiary' ? (
        <span className="relative inline-block">
          {label}
          <span
            className="absolute inset-x-0 bottom-0 h-[2px] bg-primary-200 origin-center transition-transform duration-250 ease-out [transform:translateY(var(--underline-offset))_scaleX(0)] group-hover:[transform:translateY(var(--underline-offset))_scaleX(1)] group-active:bg-primary-600"
            style={
              {
                '--underline-offset': tertiaryUnderlineOffset[size],
              } as React.CSSProperties
            }
          />
        </span>
      ) : (
        label
      )

    if (!hasIcon || !cleanButton.icon) return textElement

    return (
      <>
        {iconPlacement === 'left' && <Icon icon={cleanButton.icon} size={iconSizes[size]} />}
        {textElement}
        {iconPlacement === 'right' && <Icon icon={cleanButton.icon} size={iconSizes[size]} />}
      </>
    )
  }

  // If button has a link child, render as Link component
  if (cleanButton.link) {
    // Priority: children > label > page name > post title > 'Button'
    const linkLabel =
      children ||
      cleanButton.link?.label ||
      cleanButton.link?.internalPage?.name ||
      cleanButton.link?.internalPost?.title ||
      'Button'

    return (
      <Link link={cleanButton.link} tracking={tracking} className={finalClassName}>
        {renderContent(linkLabel)}
      </Link>
    )
  }

  // If asChild is true and children is a Link component, render with button styles
  if (asChild && children && React.isValidElement(children) && isLinkElement(children)) {
    return (
      <Link
        link={children.props.link}
        tracking={tracking}
        className={cn(finalClassName, children.props.className)}
      >
        {renderContent(children.props.link?.label || 'Button')}
      </Link>
    )
  }

  // Otherwise render as a button element
  return (
    <button type="button" className={finalClassName} {...buttonProps} onClick={handleButtonClick}>
      {renderContent(children || 'Button')}
    </button>
  )
}
